import _ from 'lodash';
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field } from 'redux-form';
import { components, contentfulModels } from '@glowforge/ui-common';


import SubmitActions from 'app/common/components/forms/submit-actions';
import { machinePropType } from 'app/selectors/machines';
import { getInvalidEmails } from 'app/util';
import features from 'app/common/containers/features';
import MachineAccessField from 'app/containers/settings/machine-access-field';
import MachineCheckboxGroup from './machine-checkbox-group';

import './user-invite-form.less';

const { pageStringsPropType } = contentfulModels;
const { Markdown, SVG, Text } = components;

export const formValidation = (values) => {
  const errors = {};
  if (!values.email) {
    errors.email = 'Required';
  } else {
    const invalidEmails = getInvalidEmails(values.email).filter(Boolean);

    if (invalidEmails.length > 1) {
      errors.email = `${invalidEmails.join(', ')} are not valid emails.`;
    } else if (invalidEmails.length === 1) {
      errors.email = `${invalidEmails[0]} is not a valid email.`;
    }
  }

  if (!_.size(values.machines)) {
    errors.machines = 'Please select a machine';
  }
  return errors;
};

const exceedsAddedUserCount = totalAllowed => value => {
  if (!value) return null;

  const emailCount = value.split(',').filter(Boolean).length;
  // May change based on product review
  return totalAllowed < emailCount ? 'You have filled up your additional seats' : null;
}

export const UserInviteForm = (props) => {
  const {
    machines,
    managedMachines,
    isLoading,
    features: featureFlags,
    handleSubmit,
    onCancel,
    email,
    pageStrings,
    pageMarkdownStrings,
    formInvalid,
    subscription,
    addedUsers,
  } = props;

  if (isLoading) {
    return <div className='loadingIndicator'><SVG id='spinner' /></div>;
  }

  if (_.isEmpty(machines)) {
    return (
      <div className='UserInviteForm empty'>
        <Markdown source={pageMarkdownStrings['settings-users-tab-no-machines']} />
      </div>
    );
  }

  if (_.isEmpty(managedMachines)) {
    return (
      <div className='UserInviteForm empty'>
        <Markdown source={pageMarkdownStrings['settings-users-tab-not-a-manager']} />
      </div>
    );
  }

  const isSubscriptionOwner = subscription.attributes && subscription.attributes.external;
  const plan = isSubscriptionOwner ? subscription.attributes.external.plan_id : '';
  const isEduAccount = plan.toLowerCase().includes('edu') && featureFlags.MIZZLE_FRIZZLE;

  const emailsAddedCount = useCallback(values => {
    if (isSubscriptionOwner && isEduAccount) {
      const totalUsersLeft = subscription.attributes.total_users - addedUsers.length;
      return exceedsAddedUserCount(totalUsersLeft)(values);
    }
  }, [subscription]);

  let machineRows;
  let fieldComponent = MachineCheckboxGroup;
  if (featureFlags.USER_ROLE_MANAGEMENT) fieldComponent = MachineAccessField;
  if (managedMachines.length > 0 || featureFlags.USER_ROLE_MANAGEMENT) {
    machineRows = (
      <React.Fragment>
        <h2 className='h3'>Which Printers should they have access to?</h2>
        <Field
          name='machines'
          component={fieldComponent}
          email={email}
          grants={isEduAccount ? ['added_student'] : []}
          options={managedMachines}
          pageStrings={pageStrings} />
      </React.Fragment>
    );
  }

  return (
    <form className='UserInviteForm' onSubmit={handleSubmit}>
      <h2 className='h3'>
        Who do you want to add{managedMachines.length === 1 ? ' to your Glowforge' : ''}?
      </h2>
      <div className='hint'>
        To add multiple users, separate each email by comma.
      </div>
      <Field
        className='email'
        name='email'
        placeholder='enter email addresses (comma separated)'
        validate={[emailsAddedCount]}
        component={Text} />
      {machineRows}
      <SubmitActions
        {...props}
        disabled={formInvalid}
        submitText='Add Users'
        successMessage='Invite Sent!'
        inProgressMessage='Sending...'
        onCancel={onCancel} />
    </form>
  );
};

UserInviteForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  machines: PropTypes.arrayOf(PropTypes.shape(machinePropType)),
  managedMachines: PropTypes.arrayOf(PropTypes.shape(machinePropType)),
  email: PropTypes.string,
  features: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  pageStrings: pageStringsPropType.isRequired,
  pageMarkdownStrings: pageStringsPropType.isRequired,
  formInvalid: PropTypes.bool,
  subscription: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  addedUsers: PropTypes.array, // eslint-disable-line react/forbid-prop-types
};

UserInviteForm.defaultProps = {
  machines: [],
  managedMachines: [],
  machineListError: null,
  isLoading: false,
  onCancel: () => {},
  email: '',
  features: {},
  formInvalid: false,
  subscription: {},
  addedUsers: [],
};

const UserInviteReduxForm = reduxForm({
  form: 'userInviteForm',
  validate: formValidation,
})(UserInviteForm);

export default features(UserInviteReduxForm);



// WEBPACK FOOTER //
// ./app/components/settings/user-invite-form.jsx